import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  adminListVacancySources,
  adminListVacancyRuns,
  adminListVacancySubscribers,
  adminUpsertVacancySource,
  adminDeleteVacancySource,
  adminRunVacancySource,
  adminRunAllVacancySources,
} from "@/lib/api/vacancy-sources.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/admin/vacancy-sources")({
  component: VacancySourcesAdmin,
});

function VacancySourcesAdmin() {
  const qc = useQueryClient();
  const listFn = useServerFn(adminListVacancySources);
  const runsFn = useServerFn(adminListVacancyRuns);
  const subsFn = useServerFn(adminListVacancySubscribers);
  const upsertFn = useServerFn(adminUpsertVacancySource);
  const deleteFn = useServerFn(adminDeleteVacancySource);
  const runOneFn = useServerFn(adminRunVacancySource);
  const runAllFn = useServerFn(adminRunAllVacancySources);

  const sources = useQuery({ queryKey: ["vac-sources"], queryFn: () => listFn() });
  const runs = useQuery({ queryKey: ["vac-runs"], queryFn: () => runsFn() });
  const subs = useQuery({ queryKey: ["vac-subs"], queryFn: () => subsFn() });

  const [editing, setEditing] = useState<any | null>(null);

  const save = useMutation({
    mutationFn: async (data: any) => upsertFn({ data }),
    onSuccess: () => {
      toast.success("Saved");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["vac-sources"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const del = useMutation({
    mutationFn: async (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["vac-sources"] });
    },
  });

  const runOne = useMutation({
    mutationFn: async (id: string) => runOneFn({ data: { id } }),
    onSuccess: (res: any) => {
      toast.success(`Crawl ${res.status}: ${res.jobsNew} new, ${res.notificationsSent} notified`);
      qc.invalidateQueries({ queryKey: ["vac-sources"] });
      qc.invalidateQueries({ queryKey: ["vac-runs"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Crawl failed"),
  });

  const runAll = useMutation({
    mutationFn: async () => runAllFn(),
    onSuccess: (res: any) => {
      toast.success(`Crawled ${res.length} sources`);
      qc.invalidateQueries({ queryKey: ["vac-sources"] });
      qc.invalidateQueries({ queryKey: ["vac-runs"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Crawl failed"),
  });

  return (
    <div className="container-narrow py-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-navy">Vacancy sources</h1>
          <p className="text-sm text-muted-foreground">
            Automated crawler that visits company career pages, detects new
            vacancies, saves them, and emails subscribers. Runs every 30 minutes.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => runAll.mutate()}
            disabled={runAll.isPending}
            className="rounded bg-navy px-3 py-2 text-sm text-navy-foreground disabled:opacity-60"
          >
            {runAll.isPending ? "Crawling…" : "Crawl all now"}
          </button>
          <button
            onClick={() =>
              setEditing({
                employer: "",
                careers_url: "",
                sector: "",
                is_active: true,
                respect_robots: true,
                crawl_interval_minutes: 45,
                notes: "",
              })
            }
            className="rounded border border-navy px-3 py-2 text-sm text-navy"
          >
            + Add source
          </button>
        </div>
      </div>

      {editing && (
        <SourceForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={(v) => save.mutate(v)}
          saving={save.isPending}
        />
      )}

      <section className="mt-6 rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-3">Employer</th>
              <th className="p-3">Last crawl</th>
              <th className="p-3">Status</th>
              <th className="p-3">Interval</th>
              <th className="p-3">Active</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.isLoading && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {sources.data?.map((s: any) => (
              <tr key={s.id} className="border-b border-border/60">
                <td className="p-3">
                  <div className="font-medium text-navy">{s.employer}</div>
                  <a
                    href={s.careers_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-muted-foreground hover:underline"
                  >
                    {s.careers_url}
                  </a>
                  {s.last_error && (
                    <div className="mt-1 text-xs text-red-600">Err: {s.last_error}</div>
                  )}
                </td>
                <td className="p-3 text-xs">
                  {s.last_crawled_at ? new Date(s.last_crawled_at).toLocaleString() : "—"}
                </td>
                <td className="p-3 text-xs">{s.last_status ?? "—"}</td>
                <td className="p-3 text-xs">{s.crawl_interval_minutes} min</td>
                <td className="p-3 text-xs">{s.is_active ? "Yes" : "No"}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      onClick={() => runOne.mutate(s.id)}
                      disabled={runOne.isPending}
                      className="rounded border border-navy px-2 py-1 text-xs text-navy disabled:opacity-60"
                    >
                      Crawl now
                    </button>
                    <button
                      onClick={() => setEditing(s)}
                      className="rounded border border-input px-2 py-1 text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => confirm("Delete source?") && del.mutate(s.id)}
                      className="rounded border border-red-300 px-2 py-1 text-xs text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sources.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-muted-foreground">
                  No sources yet — add your first company careers page.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="mt-8 grid gap-6 md:grid-cols-2">
        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="font-display text-lg text-navy">Recent crawl runs</h2>
          <ul className="mt-3 space-y-1 text-xs">
            {runs.data?.slice(0, 15).map((r: any) => (
              <li key={r.id} className="flex justify-between gap-2 border-b border-border/40 pb-1">
                <span>{new Date(r.started_at).toLocaleString()}</span>
                <span
                  className={
                    r.status === "ok"
                      ? "text-emerald-600"
                      : r.status === "error"
                        ? "text-red-600"
                        : "text-muted-foreground"
                  }
                >
                  {r.status} · new {r.jobs_new} · sent {r.notifications_sent}
                </span>
              </li>
            ))}
            {!runs.data?.length && <li className="text-muted-foreground">No runs yet.</li>}
          </ul>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <h2 className="font-display text-lg text-navy">Subscribers</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {subs.data?.filter((s: any) => s.confirmed_at && !s.unsubscribed_at).length ?? 0}{" "}
            confirmed · {subs.data?.filter((s: any) => !s.confirmed_at).length ?? 0} pending ·{" "}
            {subs.data?.filter((s: any) => s.unsubscribed_at).length ?? 0} unsubscribed
          </p>
          <ul className="mt-3 max-h-64 space-y-1 overflow-y-auto text-xs">
            {subs.data?.slice(0, 30).map((s: any) => (
              <li key={s.id} className="border-b border-border/40 pb-1">
                <span className="font-medium">{s.email}</span>
                <span className="ml-2 text-muted-foreground">
                  {s.confirmed_at ? "✓" : "…"}
                  {s.keyword ? ` · ${s.keyword}` : ""}
                  {s.location_contains ? ` · ${s.location_contains}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <p className="mt-6 text-xs text-muted-foreground">
        <Link to="/careers" className="text-navy hover:underline">
          Public careers page →
        </Link>
      </p>
    </div>
  );
}

function SourceForm({
  initial,
  onCancel,
  onSave,
  saving,
}: {
  initial: any;
  onCancel: () => void;
  onSave: (v: any) => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(initial);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  return (
    <div className="mb-6 rounded-lg border border-navy/20 bg-navy/5 p-4">
      <h3 className="font-display text-lg text-navy">
        {form.id ? "Edit source" : "Add source"}
      </h3>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="text-xs">
          Employer name
          <input
            className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm"
            value={form.employer ?? ""}
            onChange={(e) => set("employer", e.target.value)}
          />
        </label>
        <label className="text-xs">
          Careers page URL
          <input
            className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm"
            value={form.careers_url ?? ""}
            onChange={(e) => set("careers_url", e.target.value)}
          />
        </label>
        <label className="text-xs">
          Sector (optional)
          <input
            className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm"
            value={form.sector ?? ""}
            onChange={(e) => set("sector", e.target.value)}
          />
        </label>
        <label className="text-xs">
          Crawl interval (minutes)
          <input
            type="number"
            min={15}
            max={1440}
            className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm"
            value={form.crawl_interval_minutes ?? 45}
            onChange={(e) => set("crawl_interval_minutes", Number(e.target.value))}
          />
        </label>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={!!form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
          />
          Active
        </label>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={!!form.respect_robots}
            onChange={(e) => set("respect_robots", e.target.checked)}
          />
          Respect robots.txt
        </label>
        <label className="col-span-full text-xs">
          Notes
          <textarea
            rows={2}
            className="mt-1 w-full rounded border border-input bg-background px-2 py-1 text-sm"
            value={form.notes ?? ""}
            onChange={(e) => set("notes", e.target.value)}
          />
        </label>
      </div>
      <div className="mt-4 flex gap-2">
        <button
          disabled={saving}
          onClick={() => onSave(form)}
          className="rounded bg-navy px-3 py-2 text-sm text-navy-foreground disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button onClick={onCancel} className="rounded border border-input px-3 py-2 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}
